import React from "react";
import { CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RiInformationLine, RiCheckLine } from "@remixicon/react";
import { loadFromLocalStorage, saveToLocalStorage } from "@/utils/localStorage";

export interface FooterInfoProps {
  /** The message/description to display */
  description: string;
  /** Background color for the banner (hex or Tailwind class) */
  backgroundColor?: string;
  /** Text color (hex or Tailwind class) */
  textColor?: string;
  /** Icon background color (hex or Tailwind class) */
  iconBgColor?: string;
  /** Icon color (hex or Tailwind class) */
  iconColor?: string;
  /** Border color (hex or Tailwind class) */
  borderColor?: string;
  /** Button text */
  buttonText?: string;
  /** Button icon color (hex or Tailwind class) */
  buttonIconColor?: string;
  /** Whether to show the dismiss button */
  showButton?: boolean;
  /** Custom icon component (defaults to RiInformationLine) */
  icon?: React.ReactNode;
  /** Callback when button is clicked */
  onDismiss?: () => void;
  /** localStorage key for persisting dismissal state */
  storageKey?: string;
  /** Whether the footer is dismissed (controlled) */
  isDismissed?: boolean;
  /** Callback when dismissed state changes */
  onDismissedChange?: (dismissed: boolean) => void;
}

export const FooterInfo = ({
  description,
  backgroundColor = "#E7F3FF",
  textColor = "#0071D8",
  iconBgColor = "white",
  iconColor = "#2A94F4",
  borderColor,
  buttonText = "Okay, got it",
  buttonIconColor = "#FF6A33",
  showButton = true,
  icon,
  onDismiss,
  storageKey,
  isDismissed: controlledIsDismissed,
  onDismissedChange,
}: FooterInfoProps) => {
  // Use controlled state if provided, otherwise use localStorage
  const [internalIsDismissed, setInternalIsDismissed] = React.useState(() => {
    if (controlledIsDismissed !== undefined) {
      return controlledIsDismissed;
    }
    if (storageKey) {
      return loadFromLocalStorage<boolean>(storageKey, false);
    }
    return false;
  });

  const isDismissed = controlledIsDismissed !== undefined ? controlledIsDismissed : internalIsDismissed;

  const handleDismiss = () => {
    const newDismissedState = true;
    
    if (controlledIsDismissed === undefined) {
      setInternalIsDismissed(newDismissedState);
    }
    
    if (storageKey) {
      saveToLocalStorage(storageKey, newDismissedState);
    }
    
    if (onDismissedChange) {
      onDismissedChange(newDismissedState);
    }
    
    if (onDismiss) {
      onDismiss();
    }
  };

  if (isDismissed) {
    return null;
  }

  return (
    <CardFooter className="p-0">
      <div
        className="flex h-[38px] border-none p-1 justify-between items-center self-stretch w-full gap-3 rounded-[12px] squircle-mask squircle-radius-[12px] squircle-smooth-[1]"
        style={{
          backgroundColor
        }}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div
            className="flex justify-center items-center squircle-mask squircle-radius-[8px] w-[30px] h-[30px]"
            style={{ backgroundColor: iconBgColor }}
          >
            {icon || <RiInformationLine size={18} className="shrink-0" style={{ color: iconColor }} />}
          </div>
          <p className="text-[12px] font-medium leading-[16px]" style={{ color: textColor }}>
            {description}
          </p>
        </div>
        {showButton && (
          <Button
            type="button"
            onClick={handleDismiss}
            className="flex items-center gap-[6px] h-[30px] px-[14px] py-[8px] rounded-[8px] border bg-white shadow-sm"
            style={borderColor ? { borderColor } : undefined}
          >
            <RiCheckLine size={14} className="shrink-0" style={{ color: buttonIconColor }} />
            <span className="text-brand-primary-800 text-[11px] font-medium">{buttonText}</span>
          </Button>
        )}
      </div>
    </CardFooter>
  );
};
